import { useCallback, useMemo, useState } from "react";
import type {
  ColumnPin,
  ColumnPreference,
  TableColumnDef,
  TableColumnPreferencesState,
} from "./types";

const STORAGE_PREFIX = "jbf-table-columns:";

function isHiddenFromColumnMenu<T>(
  col: TableColumnDef<T> | undefined
): boolean {
  if (!col) {
    return false;
  }
  return col.hideFromDisplay === true || col.hideInColumnMenu === true;
}

/** Only lockVisibility columns (e.g. Sr. No) cannot be hidden */
function mustStayVisible<T>(col: TableColumnDef<T> | undefined): boolean {
  return col?.lockVisibility === true;
}

function getEffectivePin<T>(
  pref: ColumnPreference | undefined,
  _col?: TableColumnDef<T>
): ColumnPin {
  return pref?.pin === "left" || pref?.pin === "right" ? pref.pin : null;
}

function buildDefaultPreferences<T>(
  columns: TableColumnDef<T>[]
): ColumnPreference[] {
  return columns
    .filter(col => !isHiddenFromColumnMenu(col))
    .map(col => ({
      id: col.id,
      visible: col.defaultVisible !== false,
      pin: null,
    }));
}

function mergePreference<T>(
  saved: ColumnPreference,
  fallback: ColumnPreference,
  def?: TableColumnDef<T>
): ColumnPreference {
  if (mustStayVisible(def)) {
    return {
      id: fallback.id,
      visible: true,
      pin: saved.pin === "left" || saved.pin === "right" ? saved.pin : null,
    };
  }
  return {
    id: fallback.id,
    visible: saved.visible,
    pin: saved.pin === "left" || saved.pin === "right" ? saved.pin : null,
  };
}

function loadPreferences<T>(
  tableId: string,
  defaults: ColumnPreference[],
  columnDefs: TableColumnDef<T>[]
): ColumnPreference[] {
  const defById = new Map(columnDefs.map(c => [c.id, c]));
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${tableId}`);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as TableColumnPreferencesState;
    if (!Array.isArray(parsed.columns)) {
      return defaults;
    }

    const defaultById = new Map(defaults.map(c => [c.id, c]));
    const ordered: ColumnPreference[] = [];

    for (const saved of parsed.columns) {
      const fallback = defaultById.get(saved.id);
      if (fallback) {
        ordered.push(mergePreference(saved, fallback, defById.get(saved.id)));
        defaultById.delete(saved.id);
      }
    }

    for (const remaining of defaultById.values()) {
      ordered.push(remaining);
    }

    for (const col of columnDefs) {
      if (isHiddenFromColumnMenu(col)) {
        continue;
      }
      if (!ordered.some(p => p.id === col.id)) {
        ordered.push({
          id: col.id,
          visible: col.defaultVisible !== false,
          pin: null,
        });
      }
    }

    return ordered.length > 0 ? ordered : defaults;
  } catch {
    return defaults;
  }
}

function savePreferences(tableId: string, columns: ColumnPreference[]) {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${tableId}`,
      JSON.stringify({ columns } satisfies TableColumnPreferencesState)
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export interface UseTableColumnPreferencesResult<T> {
  displayColumns: TableColumnDef<T>[];
  menuColumns: ColumnPreference[];
  setVisible: (id: string, visible: boolean) => void;
  setAllVisible: (visible: boolean) => void;
  setPin: (id: string, pin: ColumnPin) => void;
  reorderColumns: (activeId: string, overId: string) => void;
  reset: () => void;
  getStickyStyle: (columnId: string) => React.CSSProperties | undefined;
  getPinCellClassName: (columnId: string) => string | undefined;
}

export function useTableColumnPreferences<T>(
  tableId: string,
  columnDefs: TableColumnDef<T>[]
): UseTableColumnPreferencesResult<T> {
  const defaultPreferences = useMemo(
    () => buildDefaultPreferences(columnDefs),
    [columnDefs]
  );

  const [preferences, setPreferences] = useState<ColumnPreference[]>(() =>
    loadPreferences(tableId, defaultPreferences, columnDefs)
  );

  const defById = useMemo(
    () => new Map(columnDefs.map(c => [c.id, c])),
    [columnDefs]
  );

  const prefById = useMemo(
    () => new Map(preferences.map(p => [p.id, p])),
    [preferences]
  );

  const persist = useCallback(
    (next: ColumnPreference[]) => {
      setPreferences(next);
      savePreferences(tableId, next);
    },
    [tableId]
  );

  const setVisible = useCallback(
    (id: string, visible: boolean) => {
      const def = defById.get(id);
      if (def?.lockVisibility) {
        return;
      }

      const next = preferences.map(p => (p.id === id ? { ...p, visible } : p));
      const visibleCount = next.filter(p => p.visible).length;
      if (visibleCount === 0) {
        return;
      }
      persist(next);
    },
    [defById, preferences, persist]
  );

  const setAllVisible = useCallback(
    (visible: boolean) => {
      if (visible) {
        persist(preferences.map(p => ({ ...p, visible: true })));
        return;
      }

      const next = preferences.map(p => {
        const def = defById.get(p.id);
        if (mustStayVisible(def)) {
          return { ...p, visible: true };
        }
        return { ...p, visible: false };
      });
      if (next.filter(p => p.visible).length === 0) {
        return;
      }
      persist(next);
    },
    [defById, preferences, persist]
  );

  const setPin = useCallback(
    (id: string, pin: ColumnPin) => {
      const next = preferences.map(p => {
        if (p.id !== id) {
          return p;
        }
        const current = p.pin;
        const nextPin = current === pin ? null : pin;
        return { ...p, pin: nextPin };
      });
      persist(next);
    },
    [preferences, persist]
  );

  const reorderColumns = useCallback(
    (activeId: string, overId: string) => {
      const oldIndex = preferences.findIndex(p => p.id === activeId);
      const newIndex = preferences.findIndex(p => p.id === overId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
        return;
      }
      const next = [...preferences];
      const [removed] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, removed);
      persist(next);
    },
    [preferences, persist]
  );

  const reset = useCallback(() => {
    persist(defaultPreferences);
  }, [defaultPreferences, persist]);

  const displayColumns = useMemo(() => {
    const hiddenFromMenu = columnDefs.filter(c => c.hideFromDisplay);
    const leftPinned: TableColumnDef<T>[] = [];
    const center: TableColumnDef<T>[] = [];
    const rightPinned: TableColumnDef<T>[] = [];

    for (const pref of preferences) {
      const col = defById.get(pref.id);
      if (!col || col.hideFromDisplay) {
        continue;
      }
      if (!pref.visible && !mustStayVisible(col)) {
        continue;
      }

      const pin = getEffectivePin(pref, col);
      if (pin === "left") {
        leftPinned.push(col);
      } else if (pin === "right") {
        rightPinned.push(col);
      } else {
        center.push(col);
      }
    }

    return [...hiddenFromMenu, ...leftPinned, ...center, ...rightPinned];
  }, [columnDefs, preferences, defById]);

  const getStickyStyle = useCallback(
    (columnId: string): React.CSSProperties | undefined => {
      const pref = prefById.get(columnId);
      const pin = getEffectivePin(pref, defById.get(columnId));
      if (!pin) {
        return undefined;
      }

      const visibleOrdered = displayColumns.filter(c => !c.hideFromDisplay);
      const width = (id: string) => defById.get(id)?.width ?? 140;

      if (pin === "left") {
        const leftCols = visibleOrdered.filter(
          c => getEffectivePin(prefById.get(c.id), c) === "left"
        );
        const idx = leftCols.findIndex(c => c.id === columnId);
        if (idx < 0) {
          return undefined;
        }
        const left = leftCols
          .slice(0, idx)
          .reduce((sum, c) => sum + width(c.id), 0);
        return {
          position: "sticky",
          left,
          zIndex: 20,
          backgroundColor: "hsl(var(--background))",
        };
      }

      const rightCols = visibleOrdered.filter(
        c => getEffectivePin(prefById.get(c.id), c) === "right"
      );
      const idx = rightCols.findIndex(c => c.id === columnId);
      if (idx < 0) {
        return undefined;
      }
      const right = rightCols
        .slice(idx + 1)
        .reduce((sum, c) => sum + width(c.id), 0);
      return {
        position: "sticky",
        right,
        zIndex: 20,
        backgroundColor: "hsl(var(--background))",
      };
    },
    [prefById, defById, displayColumns]
  );

  const getPinCellClassName = useCallback(
    (columnId: string): string | undefined => {
      const pin = getEffectivePin(
        prefById.get(columnId),
        defById.get(columnId)
      );
      if (!pin) {
        return undefined;
      }

      const visibleOrdered = displayColumns.filter(c => !c.hideFromDisplay);
      const pinnedClass =
        "relative z-20 bg-background before:pointer-events-none before:absolute before:top-0 before:h-full before:w-3";

      if (pin === "left") {
        const leftCols = visibleOrdered.filter(
          c => getEffectivePin(prefById.get(c.id), c) === "left"
        );
        const isLastLeft = leftCols[leftCols.length - 1]?.id === columnId;
        if (!isLastLeft) {
          return "sticky z-20 bg-background";
        }
        return `${pinnedClass} before:-right-3 before:bg-gradient-to-r before:from-black/[0.06] before:to-transparent`;
      }

      const rightCols = visibleOrdered.filter(
        c => getEffectivePin(prefById.get(c.id), c) === "right"
      );
      const isFirstRight = rightCols[0]?.id === columnId;
      if (!isFirstRight) {
        return "sticky z-20 bg-background";
      }
      return `${pinnedClass} before:-left-3 before:bg-gradient-to-l before:from-black/[0.06] before:to-transparent`;
    },
    [prefById, defById, displayColumns]
  );

  return {
    displayColumns,
    menuColumns: preferences,
    setVisible,
    setAllVisible,
    setPin,
    reorderColumns,
    reset,
    getStickyStyle,
    getPinCellClassName,
  };
}
